import React, { useState, useEffect, useRef } from 'react';
import { cleanApiBaseUrl, rag, records } from '../lib/api';
import toast from 'react-hot-toast';
import { Send, Sparkles, FileText, Database, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

const EXAMPLE_QUESTIONS = [
  "What did the council decide about the parks budget in 2022?",
  "Which ordinances relate to zoning changes in downtown?",
  "What were the key points from the city attorney memo on contract review?",
  "Summarize the most recent meeting minutes",
  "What resolutions were passed regarding infrastructure?",
];

function SourceCard({ source, apiBase }) {
  return (
    <a href={`${apiBase}/uploads/${source.file_path}`} target="_blank" rel="noopener noreferrer"
      className="flex items-start gap-2.5 p-3 bg-white rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
      <FileText size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-gray-800 truncate group-hover:text-blue-700">{source.title}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {source.document_type && <span className="text-xs text-gray-400">{source.document_type}</span>}
          {source.department && <span className="text-xs text-gray-300">·</span>}
          {source.department && <span className="text-xs text-gray-400">{source.department}</span>}
        </div>
      </div>
      <span className="text-xs text-teal-600 font-medium flex-shrink-0 bg-teal-50 px-1.5 py-0.5 rounded-full">{source.similarity}%</span>
    </a>
  );
}

export default function AskPage() {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [indexing, setIndexing] = useState(false);
  const [cityName, setCityName] = useState('');
  const [exampleQuestions, setExampleQuestions] = useState(EXAMPLE_QUESTIONS);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const apiBase = cleanApiBaseUrl();

  useEffect(() => {
    rag.status().then(r => setStatus(r.data)).catch(() => {});
    inputRef.current?.focus();

    // Fetch city config for branding
    const apiBaseUrl = cleanApiBaseUrl();
    fetch(`${apiBaseUrl}/api/config`)
      .then(r => r.json())
      .then(data => {
        if (data.city_name) setCityName(data.city_name);
        if (Array.isArray(data.ask_examples) && data.ask_examples.length) setExampleQuestions(data.ask_examples);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const ask = async (q) => {
    const question_text = q || question.trim();
    if (!question_text) return;
    setQuestion('');
    setLoading(true);

    setMessages(m => [...m, { role: 'user', text: question_text }]);

    try {
      const { data } = await rag.ask(question_text, 5);
      setMessages(m => [...m, {
        role: 'assistant',
        text: data.answer,
        sources: data.sources || [],
        duration_ms: data.duration_ms
      }]);
    } catch (e) {
      setMessages(m => [...m, {
        role: 'assistant',
        text: 'Sorry, I encountered an error processing your question. Make sure Ollama is running and records have been indexed.',
        sources: [],
        isError: true
      }]);
      toast.error('RAG query failed');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleIndexAll = async () => {
    setIndexing(true);
    try {
      const { data } = await rag.ingestAll();
      toast.success(`Indexing ${data.queued} records in background`);
      setTimeout(() => rag.status().then(r => setStatus(r.data)), 3000);
    } catch (e) {
      toast.error('Indexing failed');
    } finally {
      setIndexing(false);
    }
  };

  const ollamaOk = status?.ollama?.ok;

  return (
    <div className="flex flex-col h-full max-h-screen">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
            <Sparkles size={14} className="text-white" />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Ask {cityName && cityName !== 'Generic' ? `${cityName} ` : ''}records</h1>
            <p className="text-xs text-gray-400">
              {status?.ollama?.embed_model && status?.ollama?.chat_model
                ? `Semantic search powered by ${status.ollama.embed_model} + ${status.ollama.chat_model}`
                : 'Semantic search powered by on-premises AI'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Ollama status */}
          <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border
            ${ollamaOk ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {ollamaOk
              ? <><CheckCircle size={11} /> Ollama ready</>
              : <><AlertCircle size={11} /> Ollama offline</>}
          </div>

          {/* Index status */}
          {status && (
            <div className="text-xs text-gray-400">
              {status.indexed_records}/{status.indexable_records} records indexed
            </div>
          )}

          <button onClick={handleIndexAll} disabled={indexing || !ollamaOk}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">
            <Database size={12} className={indexing ? 'animate-pulse' : ''} />
            {indexing ? 'Indexing...' : 'Index all'}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto pt-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mx-auto mb-3">
                <Sparkles size={20} className="text-purple-500" />
              </div>
              <h2 className="text-base font-medium text-gray-800">Ask anything about {cityName && cityName !== 'Generic' ? `${cityName} ` : 'city '}records</h2>
              <p className="text-sm text-gray-400 mt-1">Semantic search finds relevant content even if you don't know the exact words</p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {exampleQuestions.map((q, i) => (
                <button key={i} onClick={() => ask(q)} disabled={loading || !ollamaOk}
                  className="text-left px-4 py-3 bg-white rounded-xl border border-gray-100 text-sm text-gray-600 hover:border-blue-200 hover:text-blue-700 hover:bg-blue-50/30 transition-all disabled:opacity-40">
                  "{q}"
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} max-w-4xl mx-auto w-full`}>
            {msg.role === 'user' ? (
              <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-lg text-sm">
                {msg.text}
              </div>
            ) : (
              <div className="flex-1 max-w-2xl">
                <div className={`rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed
                  ${msg.isError ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-white border border-gray-100 text-gray-800'}`}>
                  <div>{msg.isError ? msg.text : <ReactMarkdown components={{
                    p: ({children}) => <p className="mb-2">{children}</p>,
                    strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    ul: ({children}) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                    li: ({children}) => <li>{children}</li>,
                  }}>{msg.text}</ReactMarkdown>}</div>
                  {msg.duration_ms && (
                    <div className="text-xs text-gray-300 mt-2">{(msg.duration_ms / 1000).toFixed(1)}s</div>
                  )}
                </div>
                {msg.sources?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-400 mb-1.5 ml-1">Sources ({msg.sources.length})</div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {msg.sources.map((s, j) => (
                        <SourceCard key={j} source={s} apiBase={apiBase} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start max-w-4xl mx-auto w-full">
            <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
              <RefreshCw size={14} className="animate-spin text-purple-400" />
              <span className="text-sm text-gray-400">Searching records and generating answer...</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
        <div className="max-w-4xl mx-auto flex gap-3">
          <input ref={inputRef}
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
            disabled={loading || !ollamaOk}
            placeholder={ollamaOk ? "Ask a question about any city record..." : "Start Ollama to enable semantic search"}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-50 disabled:bg-gray-50 disabled:text-gray-400" />
          <button onClick={() => ask()} disabled={loading || !question.trim() || !ollamaOk}
            className="w-11 h-11 bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
            <Send size={15} />
          </button>
        </div>
        <div className="max-w-4xl mx-auto mt-1.5">
          <p className="text-xs text-gray-300 text-center">Answers are generated from indexed records only · Always verify against source documents</p>
        </div>
      </div>
    </div>
  );
}
